/**
 * Medication types for the CKD Companion medication management module.
 * Aligned with the Medication schema in api-spec.yaml and
 * MedicationCategory from clinical-engine-interface.ts.
 */

import i18n from '../../i18n';

export type MedicationRoute = 'oral' | 'topical' | 'subcutaneous' | 'intravenous';

// Kept in lockstep with the clinical engine's MedicationCategory enum
// (packages/clinical-engine/src/types.ts) and the backend VALID_CATEGORIES.
export type MedicationCategory =
  | 'phosphate_binder'
  | 'ace_inhibitor'
  | 'arb'
  | 'antihypertensive'
  | 'antiemetic'
  | 'appetite_stimulant'
  | 'potassium_supplement'
  | 'esa'
  | 'alkalinizing_agent'
  | 'antibiotic'
  | 'nsaid'
  | 'fluid_therapy'
  | 'other';

export interface Medication {
  id: string;
  cat_id: string;
  drug_name: string;
  dose?: number | null;
  dose_unit?: string | null;
  frequency: string;
  route: MedicationRoute;
  start_date: string;
  end_date?: string | null;
  prescribed_by?: string | null;
  category: MedicationCategory;
  notes?: string | null;
  reminder_time?: string | null;
  created_at: string;
}

export interface MedicationFormData {
  drug_name: string;
  dose: string;
  dose_unit: string;
  frequency: string;
  route: MedicationRoute;
  category: MedicationCategory;
  start_date: string;
  prescribed_by: string;
  notes: string;
  reminder_time: string | null;
}

/** Human-readable labels for medication categories (owner-friendly) */
export const CATEGORY_LABELS: Record<MedicationCategory, string> = {
  get phosphate_binder() { return i18n.t('medications.categories.phosphateBinder'); },
  get ace_inhibitor() { return i18n.t('medications.categories.aceInhibitor'); },
  get arb() { return i18n.t('medications.categories.arb'); },
  get antihypertensive() { return i18n.t('medications.categories.antihypertensive'); },
  get antiemetic() { return i18n.t('medications.categories.antiemetic'); },
  get appetite_stimulant() { return i18n.t('medications.categories.appetiteStimulant'); },
  get potassium_supplement() { return i18n.t('medications.categories.potassiumSupplement'); },
  get esa() { return i18n.t('medications.categories.esa'); },
  get alkalinizing_agent() { return i18n.t('medications.categories.alkalinizingAgent'); },
  get antibiotic() { return i18n.t('medications.categories.antibiotic'); },
  get nsaid() { return i18n.t('medications.categories.nsaid'); },
  get fluid_therapy() { return i18n.t('medications.categories.fluidTherapy'); },
  get other() { return i18n.t('medications.categories.other'); },
};

/**
 * Standing, medication-class context notes (NOT alerts). Rendered beside an
 * ACTIVE medication of the given category and tied to the active list, so a note
 * disappears automatically once the med is discontinued (the ended-meds list does
 * not render them). It is a pure function of the current medication — no stored
 * record.
 *
 * Each note is a short pointer only: the full monitoring instructions live on the
 * Guidance page (which it links to), so it does not duplicate them. Copy reflects
 * the ACEi/ARB dehydration/AKI guidance already surfaced elsewhere in the app
 * (IRIS/ISFM). Only classes with a genuine single-drug standing risk in CKD have a
 * note; combination risks (e.g. ACEi + potassium) are not tied to one medication.
 *
 * The categories that carry a note; the sentences themselves (one whole sentence
 * per category, with the drug name and the Guidance link inside it) are in the
 * locale file under `medications.list.standingNote` and rendered by MedicationList.
 */
export const MEDICATION_STANDING_NOTES: Partial<Record<MedicationCategory, true>> = {
  ace_inhibitor: true,
  arb: true,
};

/** Human-readable labels for routes (owner-friendly) */
export const ROUTE_LABELS: Record<MedicationRoute, string> = {
  get oral() { return i18n.t('medications.routes.oral'); },
  get topical() { return i18n.t('medications.routes.topical'); },
  get subcutaneous() { return i18n.t('medications.routes.subcutaneous'); },
  get intravenous() { return i18n.t('medications.routes.intravenous'); },
};

/**
 * Common frequency options. These codes are the stored VALUES (the medication's
 * `frequency` field); `frequencyLabel` gives the text shown, in the current language.
 * They were stored as English ("Once daily") until the codes; those rows were
 * rewritten (backend migrations/codes_for_breed_and_frequency.py). Keep in step
 * with the backend (src/stored_codes.py).
 */
export const FREQUENCY_OPTIONS = [
  'once_daily',
  'twice_daily',
  'three_times_daily',
  'every_other_day',
  'weekly',
  'as_needed',
  'with_meals',
];

/** The display text for a stored frequency; a value that is not a code is shown as written. */
export function frequencyLabel(frequency: string): string {
  switch (frequency) {
    case 'once_daily': return i18n.t('medications.frequencies.onceDaily');
    case 'twice_daily': return i18n.t('medications.frequencies.twiceDaily');
    case 'three_times_daily': return i18n.t('medications.frequencies.threeTimesDaily');
    case 'every_other_day': return i18n.t('medications.frequencies.everyOtherDay');
    case 'weekly': return i18n.t('medications.frequencies.weekly');
    case 'as_needed': return i18n.t('medications.frequencies.asNeeded');
    case 'with_meals': return i18n.t('medications.frequencies.withMeals');
    default: return frequency;
  }
}

// ────────────────────────────────────────
// Auto-detection: drug name → category + route
// ────────────────────────────────────────

/**
 * Common CKD medications mapped to their category and typical route.
 * Keys are lowercase for case-insensitive matching.
 * This allows the form to auto-fill category and route when the owner
 * types a drug name, removing the burden of knowing clinical terminology.
 */
interface DrugInfo {
  category: MedicationCategory;
  route: MedicationRoute;
  /**
   * Marks a drug that is a LIQUID measured by volume, so the dose-unit default
   * should be mL rather than mg. Only set for products that are confidently and
   * (near-)exclusively oral solutions in feline/canine practice — e.g. Semintra
   * (telmisartan oral solution) and capromorelin (Elura/Entyce). Drugs available
   * in both tablet and liquid form, and injectables dosed in mcg/units, are left
   * unset so the unit stays mg and the owner picks — we don't guess a form we
   * can't infer from the name alone.
   */
  form?: 'liquid';
}

export const DRUG_LOOKUP: Record<string, DrugInfo> = {
  // ACE inhibitors
  benazepril:       { category: 'ace_inhibitor', route: 'oral' },
  fortekor:         { category: 'ace_inhibitor', route: 'oral' },
  enalapril:        { category: 'ace_inhibitor', route: 'oral' },
  ramipril:         { category: 'ace_inhibitor', route: 'oral' },
  lisinopril:       { category: 'ace_inhibitor', route: 'oral' },

  // ARBs — Semintra is the feline oral SOLUTION (measured in mL); generic
  // telmisartan also exists as tablets, so it is left unmarked.
  telmisartan:      { category: 'arb', route: 'oral' },
  semintra:         { category: 'arb', route: 'oral', form: 'liquid' },

  // Antihypertensives
  amlodipine:       { category: 'antihypertensive', route: 'oral' },
  norvasc:          { category: 'antihypertensive', route: 'oral' },

  // Phosphate binders
  // Both spellings stay as LOOKUP KEYS: they match what an owner types off a
  // label, and UK products are labelled "aluminium". Display copy is US.
  'aluminum hydroxide':  { category: 'phosphate_binder', route: 'oral' },
  'aluminium hydroxide': { category: 'phosphate_binder', route: 'oral' },
  ipakitine:        { category: 'phosphate_binder', route: 'oral' },
  epakitin:         { category: 'phosphate_binder', route: 'oral' },
  pronefra:         { category: 'phosphate_binder', route: 'oral' },
  renalzin:         { category: 'phosphate_binder', route: 'oral' },
  'lanthanum carbonate': { category: 'phosphate_binder', route: 'oral' },
  'calcium carbonate':   { category: 'phosphate_binder', route: 'oral' },
  'sevelamer':      { category: 'phosphate_binder', route: 'oral' },

  // Antiemetics
  maropitant:       { category: 'antiemetic', route: 'oral' },
  cerenia:          { category: 'antiemetic', route: 'oral' },
  ondansetron:      { category: 'antiemetic', route: 'oral' },
  famotidine:       { category: 'antiemetic', route: 'oral' },
  omeprazole:       { category: 'antiemetic', route: 'oral' },

  // Appetite stimulants
  mirtazapine:      { category: 'appetite_stimulant', route: 'oral' },
  mirataz:          { category: 'appetite_stimulant', route: 'topical' },
  cyproheptadine:   { category: 'appetite_stimulant', route: 'oral' },
  // capromorelin (Elura/Entyce) is only available as an oral solution (mL).
  capromorelin:     { category: 'appetite_stimulant', route: 'oral', form: 'liquid' },
  elura:            { category: 'appetite_stimulant', route: 'oral', form: 'liquid' },
  entyce:           { category: 'appetite_stimulant', route: 'oral', form: 'liquid' },

  // Potassium supplements
  'potassium gluconate': { category: 'potassium_supplement', route: 'oral' },
  'potassium citrate':   { category: 'potassium_supplement', route: 'oral' },
  tumil:            { category: 'potassium_supplement', route: 'oral' },
  renal_k:          { category: 'potassium_supplement', route: 'oral' },
  'renal k':        { category: 'potassium_supplement', route: 'oral' },

  // ESA (anemia treatment)
  darbepoetin:      { category: 'esa', route: 'subcutaneous' },
  aranesp:          { category: 'esa', route: 'subcutaneous' },
  epoetin:          { category: 'esa', route: 'subcutaneous' },

  // Alkalinizing agents
  'sodium bicarbonate': { category: 'alkalinizing_agent', route: 'oral' },
  // potassium citrate is also used as alkalinizing agent but listed under potassium supplement

  // Antibiotics commonly used in CKD
  amoxicillin:      { category: 'antibiotic', route: 'oral' },
  'amoxicillin clavulanate': { category: 'antibiotic', route: 'oral' },
  clavamox:         { category: 'antibiotic', route: 'oral' },
  pradofloxacin:    { category: 'antibiotic', route: 'oral' },
  veraflox:         { category: 'antibiotic', route: 'oral' },
  marbofloxacin:    { category: 'antibiotic', route: 'oral' },

  // NSAIDs — generally contraindicated in CKD; categorising surfaces the
  // engine's nephrotoxicity alert (kept in sync with backend inference).
  meloxicam:        { category: 'nsaid', route: 'oral' },
  metacam:          { category: 'nsaid', route: 'oral' },
  robenacoxib:      { category: 'nsaid', route: 'oral' },
  onsior:           { category: 'nsaid', route: 'oral' },
};

/**
 * Look up a drug name and return the best match from the lookup table.
 * Matches against the full name first, then checks if the input contains
 * any known drug name as a substring (e.g., "Benazepril 2.5mg" matches "benazepril").
 */
export interface DetectedDrugInfo {
  category: MedicationCategory;
  route: MedicationRoute;
  /** Suggested dose unit when the drug's form is confidently known — 'mL' for
   *  liquids/oral solutions. Absent when the form can't be inferred (leave mg). */
  dose_unit?: 'mL';
}

export function detectDrugInfo(drugName: string): DetectedDrugInfo | null {
  const name = drugName.trim().toLowerCase();
  if (!name) return null;

  let match: DrugInfo | null = null;
  if (DRUG_LOOKUP[name]) {
    match = DRUG_LOOKUP[name];
  } else {
    // Check if input contains a known drug name (longest match first to prefer specificity)
    const sortedKeys = Object.keys(DRUG_LOOKUP).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
      if (name.includes(key)) {
        match = DRUG_LOOKUP[key];
        break;
      }
    }
  }

  if (!match) return null;
  return {
    category: match.category,
    route: match.route,
    ...(match.form === 'liquid' ? { dose_unit: 'mL' as const } : {}),
  };
}
